"use client";

import { createContext, useCallback, useContext, useLayoutEffect, useState } from "react";

type Theme = "light" | "dark";

const ThemeContext = createContext<{
  theme: Theme;
  setTheme: (next: Theme) => void;
}>({ theme: "dark", setTheme: () => undefined });

function paint(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.classList.toggle("light", theme === "light");
  root.setAttribute("data-theme", theme);
  root.style.colorScheme = theme;
  document.body?.classList.toggle("light", theme === "light");
  document.body?.classList.toggle("dark", theme === "dark");
  window.localStorage.setItem("mr-theme", theme);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("dark");

  useLayoutEffect(() => {
    const saved = window.localStorage.getItem("mr-theme");
    const next = saved === "light" || saved === "dark" ? saved : "dark";
    setThemeState(next);
    paint(next);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    paint(next);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <div className={`theme-root min-h-full ${theme}`} data-theme={theme}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
